import { useEffect, useRef, useState, type TouchEvent } from "react";
import { Link } from "react-router-dom";
import { newsList } from "@/mocks/news";

const featured = newsList.slice(0, 4);

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % featured.length);
    }, 5200);
    return () => clearInterval(timer);
  }, [index]);

  const go = (next: number) => setIndex((next + featured.length) % featured.length);

  const handleTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: TouchEvent) => {
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (delta > 45) go(index - 1);
    else if (delta < -45) go(index + 1);
  };

  const current = featured[index];

  return (
    <section className="mx-auto max-w-[1280px] px-4 pt-6 md:px-8 md:pt-8">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-6">
        <div className="relative lg:col-span-2">
          <div
            className="group relative h-[360px] w-full overflow-hidden rounded-lg md:h-[480px]"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {featured.map((item, i) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  i === index ? "opacity-100" : "pointer-events-none opacity-0"
                }`}
              >
                <img
                  src={item.cover}
                  alt={`${item.title} - 91动漫`}
                  title={`${item.title} - 91动漫`}
                  className="h-full w-full object-top object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/90 via-secondary-950/40 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 w-full p-5 md:p-8">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-white">
                    <i className="ri-fire-line text-sm" />
                    最新热门
                  </span>
                  <h2 className="mt-3 max-w-2xl font-heading text-xl font-extrabold leading-snug text-white md:text-3xl">
                    {current.id === item.id ? item.title : item.title}
                  </h2>
                  <p className="mt-2 hidden max-w-xl text-sm leading-relaxed text-white/80 sm:block">
                    {item.summary}
                  </p>
                </div>
              </Link>
            ))}

            <button
              type="button"
              aria-label="上一条"
              onClick={() => go(index - 1)}
              className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-colors hover:bg-primary-500 md:h-10 md:w-10"
            >
              <i className="ri-arrow-left-s-line text-xl" />
            </button>
            <button
              type="button"
              aria-label="下一条"
              onClick={() => go(index + 1)}
              className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-colors hover:bg-primary-500 md:h-10 md:w-10"
            >
              <i className="ri-arrow-right-s-line text-xl" />
            </button>

            <div className="absolute bottom-4 right-5 flex items-center gap-2 md:bottom-8 md:right-8">
              {featured.map((item, i) => (
                <button
                  key={item.id}
                  type="button"
                  aria-label={`切换到第 ${i + 1} 条`}
                  onClick={() => go(i)}
                  className={`h-2 cursor-pointer rounded-full transition-all ${
                    i === index ? "w-6 bg-primary-500" : "w-2 bg-white/60 hover:bg-white"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 px-1">
            <i className="ri-notification-3-line text-lg text-primary-600" />
            <span className="font-heading text-sm font-bold text-foreground-900">焦点速览</span>
          </div>
          {featured.map((item, i) => (
            <Link
              key={item.id}
              to={`/news/${item.id}`}
              onMouseEnter={() => setIndex(i)}
              className={`group flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                i === index
                  ? "border-primary-300 bg-primary-50"
                  : "border-background-200 bg-background-50 hover:border-primary-200"
              }`}
            >
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-heading text-xs font-bold ${
                  i === index ? "bg-primary-500 text-white" : "bg-secondary-100 text-secondary-700"
                }`}
              >
                {i + 1}
              </span>
              <span className="min-w-0">
                <span className="line-clamp-2 block text-sm font-medium leading-snug text-foreground-800 transition-colors group-hover:text-primary-700">
                  {item.title}
                </span>
                <span className="mt-1 block text-xs text-foreground-400">{item.date}</span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}